import { DownloadOutlined, FileAddOutlined } from "@ant-design/icons";
import { Button, Card, Col, Divider, Flex, Modal, Row, Spin, Upload } from "antd";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { NameCollationsContext, UpdateNameCollationsContext } from "../../context/nameCollationsContext";
import { clientFetch } from "../../utils/clientFetch";
import { parseCsvErrorPayload, showCsvUploadErrorModal } from "../../utils/csvErrorModal";

export function NameMatching() {
  const { refreshNameCollations } = useContext(UpdateNameCollationsContext)
  const { nameCollations } = useContext(NameCollationsContext)
  const [isUploading, setIsUploading] = useState(false)
  useEffect(() => {
    refreshNameCollations()
  }, [refreshNameCollations])

  const grouped = useMemo(() => {
    return nameCollations.reduce<Record<string, string[]>>((acc, { measure_name, collation_value }) => {
      if (!acc[measure_name]) {
        acc[measure_name] = []
      }

      if (!acc[measure_name].includes(collation_value)) {
        acc[measure_name].push(collation_value)
      }
      return acc
    }, {})
  }, [nameCollations])

  const nameMatchingList = useMemo(() => {
    return Object.entries(grouped).map(([measure_name, collation_value]) => ({
      key: measure_name,
      label: measure_name,
      children: <WordList value={collation_value} />,
    }))
  }, [grouped])

  const onDownloadCsv = useCallback(async () => {
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/name-collations/download`, {
        method: "GET",
        headers: { 'Content-Type': 'application/json' },
      })

      if (!res.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'NameCollation.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

    } catch (error) {
      console.error("Error fetching name-collations.");
    }
  }, [])

  const onUploadCsv = useCallback(async (options: any) => {
    const { file, onSuccess, onError } = options
    setIsUploading(true)

    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/name-collations/upload`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const payload = await parseCsvErrorPayload(response);
        showCsvUploadErrorModal(file.name, payload);
        onError('error');
        return;
      }

      const result = await response.json()
      onSuccess(result)
      refreshNameCollations()
      Modal.success({ content: '登録が完了しました', centered: true })
    } catch (error) {
      showCsvUploadErrorModal(file.name)
      onError('error')
    } finally {
      setIsUploading(false)
    }
  }, [refreshNameCollations])

  return (
    <div className="flex flex-col h-full">
      <Spin spinning={isUploading} tip="アップロード中..." size="large" fullscreen />
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">
          名寄せ管理画面
        </p>
      </div>
      <Divider />
      <div className="h-[calc(100%-178px)]">
        <div className="flex-1 flex-col h-[calc(100vh-338px)] pr-[20px] overflow-y-auto">
          {nameMatchingList.map((el, index) => (
            <div key={el.key} className="mb-[20px]">
              <div className="flex items-stretch">
                <div className="flex self-stretch justify-end pr-[10px] min-w-[100px] mr-[20px] border-0 border-r-2 border-solid border-[#1677ff]">
                  <span className="flex items-center h-full text-[16px] font-medium">
                    <span>{el.label}</span>
                  </span>
                </div>
                <div className="flex-1 py-[5px]">{el.children}</div>
              </div>
              {nameMatchingList.length !== (index + 1) && <Divider />}
            </div>
          ))}
        </div>
      </div>
      <Divider />
      <Flex justify="center" className="space-x-[50px] w-full" >
        <Button variant="solid" color="primary" icon={<DownloadOutlined />} onClick={onDownloadCsv} className="w-[200px]" disabled={isUploading}>CSVダウンロード</Button>
        <Upload
          customRequest={onUploadCsv}
          maxCount={1}
          accept=".csv"
          showUploadList={false}
          disabled={isUploading}
        >
          <Button variant="solid" color="primary" icon={<FileAddOutlined />} className="w-[200px]" disabled={isUploading}>CSVアップロード</Button>
        </Upload>
      </Flex>
    </div>
  )
}

function WordList({ value }: { value: string[] }) {
  return (
    <div className="w-fit overflow-y-auto overflow-x-hidden">
      <Row gutter={[24, 24]}>
        {value.map((item, index) => (
          <Col key={index}>
            <Card size="small" className="shadow-sm">{item}</Card>
          </Col>
        ))}
      </Row>
    </div>
  )
}
